import { computed, onBeforeUnmount, ref } from 'vue';

export function useCampaignData(row) {
  const isComponentActive = ref(true);

  // Função para processar placeholders
  const processPlaceholders = (template, placeholders) => {
    if (!template || !placeholders) return template || '';

    let result = template;
    Object.entries(placeholders).forEach(([key, columnName]) => {
      const placeholder = `{{${key}}}`;
      result = result.replaceAll(placeholder, columnName);
    });
    return result;
  };

  // Função para destacar placeholders no template original
  const highlightPlaceholders = (text, placeholders) => {
    if (!text || !placeholders) return text || '';

    let result = text;
    Object.keys(placeholders).forEach(key => {
      const placeholder = `{{${key}}}`;
      result = result.replaceAll(
        placeholder,
        `<span class="bg-woot-100 dark:bg-woot-800 text-woot-800 dark:text-woot-200 px-1 py-0.5 rounded text-sm font-medium">${placeholder}</span>`
      );
    });
    return result;
  };

  // Função para traduzir operadores de filtro
  const translateOperator = operator => {
    if (!operator) return 'Operador desconhecido';

    // Chaves = operadores canônicos do config/filter_operators.yml (os mesmos
    // que ficam persistidos em filter_data). Operador fora daqui cai no
    // fallback e aparece cru — visível, nunca some.
    const operators = {
      equal_to: '= (igual a)',
      not_equal_to: '≠ (diferente de)',
      is_greater_than: '> (maior que)',
      is_greater_than_or_equal: '≥ (maior ou igual a)',
      is_less_than: '< (menor que)',
      is_less_than_or_equal: '≤ (menor ou igual a)',
      contains: '⊃ (contém)',
      does_not_contain: '⊅ (não contém)',
      starts_with: '^ (inicia com)',
      is_present: '∃ (está preenchido)',
      is_not_present: '∅ (está vazio)',
      is_today: '= hoje',
      due_in_days: 'vence em X dias',
      due_x_days_ago: 'venceu há X dias',
      due_in_range: 'vence entre X e Y dias',
      overdue_range: 'venceu entre X e Y dias',
    };
    return operators[operator] || operator;
  };

  // Função para formatar placeholder
  const formatPlaceholder = key => {
    return key ? `{{${key}}}` : '';
  };

  // Função para contar caracteres
  const getCharacterCount = text => {
    const length = text ? text.length : 0;
    return {
      characters: length,
      isSMSLength: length <= 160,
      isWhatsAppOptimal: length <= 1600,
    };
  };

  // Computed para extrair IDs e informações da linha
  const rowInfo = computed(() => {
    if (!isComponentActive.value || !row.value?.original) {
      return {
        campaignId: null,
        scheduleId: null,
        fullId: null,
        campaignTitle: '',
        scheduledAt: '',
      };
    }

    try {
      const data = row.value.original;
      const [campaignId, scheduleId] = data.id.split('-');

      return {
        campaignId: parseInt(campaignId),
        scheduleId: scheduleId,
        fullId: data.id,
        campaignTitle: data.title || '',
        scheduledAt: data.scheduled_at
          ? new Date(data.scheduled_at).toLocaleDateString('pt-BR', {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '',
      };
    } catch (error) {
      console.error('Erro ao processar rowInfo:', error);
      return {
        campaignId: null,
        scheduleId: null,
        fullId: null,
        campaignTitle: '',
        scheduledAt: '',
      };
    }
  });

  // Computed para todos os dados unificados
  const campaignData = computed(() => {
    if (!isComponentActive.value || !row.value?.original) {
      return {
        campaignName: '',
        hasMessage: false,
        originalMessage: '',
        processedMessage: '',
        placeholders: {},
        tabela: '',
        filtros: [],
        sendMessage: {},
        hasData: false,
      };
    }

    try {
      const { message, filter_data, title, audience } = row.value.original;

      let parsedFilterData;
      try {
        parsedFilterData =
          typeof filter_data === 'string'
            ? JSON.parse(filter_data)
            : filter_data;
      } catch (err) {
        console.error('Erro ao fazer parse de filter_data:', err);
        parsedFilterData = {};
      }

      const placeholders = parsedFilterData?.placeholders || {};
      const processedMessage = message
        ? processPlaceholders(message, placeholders)
        : '';

      return {
        campaignName: title || '',
        hasMessage: !!message,
        originalMessage: message || '',
        processedMessage,
        placeholders,
        tabela: parsedFilterData?.tabela || '',
        filtros: parsedFilterData?.filtros || [],
        sendMessage: parsedFilterData?.send_message || {},
        hasData: !!filter_data,
        audience: audience || null,
      };
    } catch (error) {
      console.error('Erro ao processar campaignData:', error);
      return {
        campaignName: '',
        hasMessage: false,
        originalMessage: '',
        processedMessage: '',
        placeholders: {},
        tabela: '',
        filtros: [],
        sendMessage: {},
        hasData: false,
        audience: null,
      };
    }
  });

  const generateSQLPreview = () => {
    if (!isComponentActive.value) {
      return 'Componente inativo';
    }

    try {
      const data = campaignData.value;

      if (!data.hasData || !data.tabela) {
        return 'SELECT * FROM tabela_nao_especificada;';
      }

      // Mapeamento de operadores do front-end para SQL
      const operatorMap = {
        equal_to: '=',
        not_equal_to: '!=',
        is_present: 'IS NOT NULL',
        is_not_present: 'IS NULL',
        contains: 'ILIKE',
        does_not_contain: 'NOT ILIKE',
        is_greater_than: '>',
        is_greater_than_or_equal: '>=',
        is_less_than: '<',
        is_less_than_or_equal: '<=',
        starts_with: 'ILIKE',
        days_before: '<=',
        is_today: '=',
        due_in_days: '=',
        due_x_days_ago: '=',
        due_in_range: 'BETWEEN',
        overdue_range: 'BETWEEN',
      };

      // Operadores que não requerem valor de entrada
      const NO_INPUT_OPTS = ['is_present', 'is_not_present'];

      // Construção da cláusula SELECT (sempre SELECT *)
      let sql = `SELECT *\nFROM ${data.tabela}`;

      // Construção da cláusula WHERE
      if (data.filtros && data.filtros.length > 0) {
        const whereConditions = data.filtros
          .map((filtro, index) => {
            const { attribute_key, filter_operator, values, query_operator } =
              filtro;

            // Validar filtro
            if (!attribute_key || !filter_operator) {
              return null;
            }
            if (
              !NO_INPUT_OPTS.includes(filter_operator) &&
              (values === null || values === undefined)
            ) {
              return null;
            }

            // Mapear operador. Sem tradução, a condição NÃO some da cláusula:
            // sumir faria o preview desenhar uma WHERE mais frouxa que a
            // configurada, e o consultor aprovaria um público que não é o que
            // vai rodar. Aparece marcada, e quem lê vê que falta suporte aqui.
            const sqlOperator = operatorMap[filter_operator];
            if (!sqlOperator) {
              return `  /* ${attribute_key}: operador "${filter_operator}" sem equivalente no preview — confira o filtro */`;
            }

            // Formatar valores com base no operador
            let formattedValue = '';
            if (!NO_INPUT_OPTS.includes(filter_operator)) {
              if (filter_operator === 'contains') {
                formattedValue = `'%${String(values).replace(/'/g, "''")}%'`;
              } else if (filter_operator === 'does_not_contain') {
                formattedValue = `'%${String(values).replace(/'/g, "''")}%'`;
              } else if (filter_operator === 'starts_with') {
                formattedValue = `'${String(values).replace(/'/g, "''")}%'`;
              } else if (filter_operator === 'days_before') {
                formattedValue = `CURRENT_DATE - INTERVAL '${parseInt(values)} days'`;
              } else if (filter_operator === 'is_today') {
                formattedValue = 'CURRENT_DATE';
              } else if (filter_operator === 'due_in_days') {
                formattedValue = `CURRENT_DATE + INTERVAL '${parseInt(values)} days'`;
              } else if (filter_operator === 'due_x_days_ago') {
                formattedValue = `CURRENT_DATE - INTERVAL '${parseInt(values)} days'`;
              } else if (filter_operator === 'due_in_range') {
                const [startDays, endDays] = Array.isArray(values)
                  ? values.map(v => parseInt(v))
                  : String(values)
                      .split(',')
                      .map(v => parseInt(v.trim()));
                formattedValue = `CURRENT_DATE + INTERVAL '${startDays} days' AND CURRENT_DATE + INTERVAL '${endDays} days'`;
              } else if (filter_operator === 'overdue_range') {
                const [startDays, endDays] = Array.isArray(values)
                  ? values.map(v => parseInt(v))
                  : String(values)
                      .split(',')
                      .map(v => parseInt(v.trim()));
                formattedValue = `CURRENT_DATE - INTERVAL '${endDays} days' AND CURRENT_DATE - INTERVAL '${startDays} days'`;
              } else if (
                ['is_greater_than', 'is_less_than'].includes(filter_operator)
              ) {
                formattedValue = String(values).replace(/'/g, "''");
              } else {
                formattedValue = `'${String(values).replace(/'/g, "''")}'`;
              }
            }

            // Construir a condição
            const condition = `${attribute_key} ${sqlOperator}${formattedValue ? ` ${formattedValue}` : ''}`;

            if (index === 0) {
              // Primeiro filtro: apenas a condição, sem operador lógico antes
              return `  ${condition}`;
            }

            // Para filtros após o primeiro, usamos o query_operator DO FILTRO ANTERIOR
            const logicalOperator = (
              data.filtros[index - 1].query_operator || 'and'
            ).toUpperCase();
            return `  ${logicalOperator} ${condition}`;
          })
          .filter(condition => condition !== null);

        if (whereConditions.length > 0) {
          sql += '\nWHERE\n' + whereConditions.join('\n');
        }
      }

      return sql + ';';
    } catch (error) {
      console.error('Erro ao gerar SQL Preview:', error);
      return 'Erro ao gerar preview SQL';
    }
  };

  // Cleanup function
  const cleanup = () => {
    isComponentActive.value = false;
  };

  // Auto cleanup quando o componente for desmontado
  onBeforeUnmount(() => {
    cleanup();
  });

  return {
    rowInfo,
    campaignData,
    processPlaceholders,
    highlightPlaceholders,
    translateOperator,
    formatPlaceholder,
    getCharacterCount,
    generateSQLPreview,
    cleanup, // Expor para cleanup manual se necessário
  };
}
